import React, { createContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styles from './PlaceOrder.module.css';
import { Alert } from '@material-ui/lab';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { setMinutes, setHours } from 'date-fns';

import { formatPhoneNumber, isRouteOptimizationEnabled } from '../../app/utils';
import { getAllSavedAddresses, getEstimatedTotal, handleGetOrdersByOrderGroup, handleOnSortOrder, handleOptimizeRoute, ordersState, setOrderGroup, setSelectedVoucher, setShowImportModal, setShowPaymentModal, setShowTopUpModal, setShowVoucherModal } from './placeOrderSlice';
import { updateOrder } from './orderAPI';
import { InfoTooltip, LoadingOverlay, SELECTED_ADDRESS_ZOOM, StopSummaryTooltip, TealSwitch, useIsCompact, VehicleSelector } from './common';
import { Topbar } from '../../components/topbar/Topbar';
import { Order } from './Order';
import { OrderGroup } from './OrderGroup';
import { DateTimeField } from './DateTimeField';
import { StopDetailsPopover } from './StopDetailsPopover';
import { loadDropOffDraft, saveDropOffDraft } from './dropOffDraft';
import { hasPendingGuestDropOffs, loadGuestDraft, takeNextGuestDropOff } from './guestDraft';
import { buildPickUpSummary } from './stopSummary';
import { ImportModal } from './ImportModal';
import { SavedAddressModal } from './SavedAddressModal';
import { VoucherModal } from './VoucherModal';
import { PaymentModal } from './PaymentModal';
import { TopUpModal } from './TopUpModal';
import { Map } from '../../components/map/Map';
import { PlaceOrderMap } from './MapWithDirectons/PlaceOrderMap';

import importIcon from "../../images/place_order/import-icon.png";
import plusIcon from "../../images/place_order/plus-icon.png";
import voucherIcon from "../../images/place_order/voucher-icon.png";
import closeIcon from "../../images/place_order/close-icon.png";
import clockIcon from "../../images/place_order/clock-icon.png";
import halalIcon from "../../images/place_order/halal-icon.png";
import targetIcon from "../../images/place_order/target-icon.png";
import savedAddressIcon from "../../images/place_order/saved-address-icon.png";
import locationPinIcon from "../../images/place_order/location-pin-icon.png";

const SortableItem = SortableElement(({ currIdx, order, editOrderOrdinal, addStop, isLast, showDropOffModal, handleAddressChange, handleOrders, handleError }) => {
    return (
        <OrderContext.Provider value={order}>
            <Order
                currIdx={currIdx}
                editOrder={editOrderOrdinal === currIdx}
                showThread={!isLast || addStop}
                showDropOffModal={(ordinal) => showDropOffModal(ordinal)}
                handleAddressChange={(location) => handleAddressChange(location)}
                handleOrders={(order) => handleOrders(order)}
                handleError={(err, errMsg) => handleError(err, errMsg)} />
        </OrderContext.Provider>
    )
});

const SortableList = SortableContainer(({ orderListState, editOrderOrdinal, addStop, showDropOffModal, handleAddressChange, handleOrders, handleError }) => {
    const orders = orderListState.orderGroup.orders;
    return (
        <div>
            {
                orders.map((order, index) => (
                    <SortableItem
                        key={`order-${order.ordinal}`}
                        index={index}
                        currIdx={index}
                        order={order}
                        editOrderOrdinal={editOrderOrdinal}
                        addStop={addStop}
                        isLast={index === orders.length - 1}
                        showDropOffModal={showDropOffModal}
                        handleAddressChange={handleAddressChange}
                        handleOrders={handleOrders}
                        handleError={handleError}
                    />
                ))
            }
        </div>
    )
});

export const OrderGroupContext = createContext();
export const OrderContext = createContext();

export function PlaceOrder() {
    const dispatch = useDispatch();
    const { id } = useParams();
    const orderListState = useSelector(ordersState);

    const [pickUpForm, setPickUpForm] = useState({
        pickUpPersonName: "",
        pickUpContactNumber: "",
        pickUpDateTime: "",
        pickUpLocation: {
            unitNumber: "",
            address: "",
            coordinate: {
                latitude: "",
                longitude: ""
            },
            city: "",
            country: ""
        }
    });
    const [dropOffForm, setDropOffForm] = useState({
        recipientName: "",
        recipientPhone: "",
        deliverDateTime: "",
        dropOffLocation: {
            unitNumber: "",
            address: "",
            coordinate: {
                latitude: "",
                longitude: ""
            },
            city: "",
            country: ""
        },
        doorToDoor: false,
        isCodEnabled: false,
        codAmount: 0,
    });
    const [addStop, setAddStop] = useState(true);
    const [enterPickUpDetails, setEnterPickUpDetails] = useState(false);
    const [enterDropOffDetails, setEnterDropOffDetails] = useState(false);
    const [editOrderOrdinal, setEditOrderOrdinal] = useState("");
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [sheetCollapsed, setSheetCollapsed] = useState(true);

    const isCompact = useIsCompact();
    const panelScrollRef = useRef(null);

    // the dialog is centred, so scroll the panel to the highlighted stop it belongs to
    const activeStopIndex = enterPickUpDetails
        ? 0
        : editOrderOrdinal !== ""
            ? Number(editOrderOrdinal) + 1
            : enterDropOffDetails
                ? (orderListState.orderGroup.orders || []).length + 1
                : null;

    useLayoutEffect(() => {
        if (activeStopIndex === null) return;
        const row = document.querySelectorAll(`.${styles.poStop}`)[activeStopIndex];
        if (row) row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [activeStopIndex]);

    // opening the details dialog on mobile needs the sheet expanded to make sense of it
    useEffect(() => {
        if (isCompact && activeStopIndex !== null) setSheetCollapsed(false);
    }, [isCompact, activeStopIndex]);

    const { latitude: pickUpLat, longitude: pickUpLng } = pickUpForm.pickUpLocation.coordinate;

    const mapFocus = useMemo(
        () => (pickUpLat !== "" && pickUpLng !== "") ? { lat: Number(pickUpLat), lng: Number(pickUpLng) } : null,
        [pickUpLat, pickUpLng]
    );

    const skipNextDraftSave = useRef(false);

    useEffect(() => {
        // retrieve all saved addresses
        dispatch(getAllSavedAddresses());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const draft = loadDropOffDraft(id);
        if (!draft) return;

        skipNextDraftSave.current = true;
        setDropOffForm(prevDropOffForm => ({ ...prevDropOffForm, ...draft }));
    }, [id]);

    useEffect(() => {
        if (skipNextDraftSave.current) {
            skipNextDraftSave.current = false;
            return;
        }
        saveDropOffDraft(id, dropOffForm);
    }, [id, dropOffForm]);

    useEffect(() => {
        if (!localStorage.refresh_token) {
            window.location = "/login";
        }
    }, []);

    // addresses captured while logged out are replayed here: pick-up once, then
    // one drop-off at a time as each stop is created on the server
    useEffect(() => {
        if (id) return;

        const draft = loadGuestDraft();
        if (!draft) return;

        setPickUpForm(prevPickUpForm => ({
            ...prevPickUpForm,
            pickUpPersonName: draft.pickUpPersonName,
            pickUpContactNumber: draft.pickUpContactNumber,
            pickUpDateTime: draft.pickUpDateTime,
            pickUpLocation: { ...prevPickUpForm.pickUpLocation, ...draft.pickUpLocation }
        }));
    }, [id]);

    const orderGroupId = orderListState.orderGroup.id;
    const pendingDropOffAddress = dropOffForm.dropOffLocation.address;

    useEffect(() => {
        if (!id || !orderGroupId || !addStop || pendingDropOffAddress !== "") return;

        const next = takeNextGuestDropOff();
        if (!next) return;

        setDropOffForm(prevDropOffForm => ({
            ...prevDropOffForm,
            ...next,
            dropOffLocation: { ...prevDropOffForm.dropOffLocation, ...next.dropOffLocation }
        }));
    }, [id, orderGroupId, addStop, pendingDropOffAddress]);

    useEffect(() => {
        if (id) {
            dispatch(handleGetOrdersByOrderGroup(id));
        }
    }, [id, dispatch]);

    useEffect(() => {
        if (id && orderListState.orderGroup.id !== null) {
            setPickUpForm(prevPickUpForm => ({
                ...prevPickUpForm,
                pickUpDateTime: orderListState.orderGroup.pickUpDateTime,
                pickUpPersonName: orderListState.orderGroup.pickUpPersonName,
                pickUpContactNumber: orderListState.orderGroup.pickUpContactNumber,
                pickUpLocation: orderListState.orderGroup.pickUpLocation
            }));
        }
    }, [id, orderListState]);

    const onPickUpDetailsChange = (unitNumber, pickUpContactNumber, pickUpPersonName) => {
        var formattedPhoneNumber = formatPhoneNumber(pickUpContactNumber);

        if (!formattedPhoneNumber) {
            return { field: "contactNumber", message: "Enter a valid sender's contact number, starting with the country code (eg. +60)." };
        }

        if (pickUpPersonName === "") {
            return { field: "personName", message: "Enter the sender's name." };
        }

        setPickUpForm(prevPickUpForm => ({
            ...prevPickUpForm,
            pickUpContactNumber: formattedPhoneNumber,
            pickUpPersonName: pickUpPersonName,
            pickUpLocation: {
                ...prevPickUpForm.pickUpLocation,
                unitNumber: unitNumber,
            }
        }));
        // Make sure redux state is in sync for smoother experience
        dispatch(setOrderGroup({
            ...orderListState.orderGroup,
            pickUpContactNumber: formattedPhoneNumber,
            pickUpPersonName: pickUpPersonName,
            pickUpLocation: {
                ...orderListState.orderGroup.pickUpLocation,
                unitNumber: unitNumber,
            }
        }));
        setEnterPickUpDetails(false);
        setError(false);
        setErrorMessage("");
    }

    const onDropOffDetailsChange = async (deliverDateTime, unitNumber, recipientPhone, recipientName, doorToDoor, isCodEnabled, codAmount) => {
        if (deliverDateTime === "") {
            return { field: "deliverDateTime", message: "Select the delivery date." };
        }

        if (new Date(deliverDateTime) - new Date() < 0) {
            return { field: "deliverDateTime", message: "Delivery date should not be in the past." };
        }

        if (new Date(deliverDateTime) - new Date(pickUpForm.pickUpDateTime) < 0) {
            return { field: "deliverDateTime", message: "Delivery date should be later than the pick-up date." };
        }

        var formattedPhoneNumber = formatPhoneNumber(recipientPhone);

        if (!formattedPhoneNumber) {
            return { field: "contactNumber", message: "Enter a valid recipient's contact number, starting with the country code (eg. +60)." };
        }

        if (recipientName === "") {
            return { field: "personName", message: "Enter the recipient's name." };
        }

        if (isCodEnabled && (codAmount === null || isNaN(codAmount) || codAmount <= 0)) {
            return { field: "codAmount", message: "Enter the COD amount to collect." };
        }

        if (enterDropOffDetails && addStop) {
            setDropOffForm(prevDropOffForm => ({
                ...prevDropOffForm,
                recipientPhone: formattedPhoneNumber,
                recipientName: recipientName,
                deliverDateTime: deliverDateTime,
                dropOffLocation: {
                    ...prevDropOffForm.dropOffLocation,
                    unitNumber: unitNumber,
                },
                doorToDoor: doorToDoor,
                isCodEnabled: isCodEnabled,
                codAmount: (codAmount * 100)
            }));

            setEnterDropOffDetails(false);
            setError(false);
            setErrorMessage('');
        } else if (editOrderOrdinal !== "") {
            // Update Order
            const order = orderListState.orderGroup.orders[editOrderOrdinal];
            const orderPayload = {
                ...order,
                pickUpDateTime: pickUpForm.pickUpDateTime,
                recipientPhone: formattedPhoneNumber,
                recipientName: recipientName,
                deliverDateTime: new Date(deliverDateTime).toISOString(),
                dropOffLocation: {
                    ...order.dropOffLocation,
                    unitNumber: unitNumber,
                },
                doorToDoor: doorToDoor,
                isCodEnabled: isCodEnabled,
                codAmount: isCodEnabled ? (codAmount * 100) : ""
            }
            await updateOrder(orderListState.orderGroup.id, orderPayload, order.id);
            dispatch(handleGetOrdersByOrderGroup(orderListState.orderGroup.id));

            setEditOrderOrdinal("");
            setError(false);
            setErrorMessage("");
        }
    }

    const onSortEnd = ({ oldIndex, newIndex }) => {
        if (oldIndex !== newIndex) {
            let orders = [...orderListState.orderGroup.orders].sort((a, b) => a.ordinal - b.ordinal);
            if (newIndex > oldIndex) {
                for (let i = oldIndex; i <= newIndex; i++) {
                    let newOrdinal = i === oldIndex ? newIndex : i - 1;
                    orders[i] = { ...orders[i], ordinal: newOrdinal };
                }
            } else {
                for (let i = oldIndex; i >= newIndex; i--) {
                    let newOrdinal = i === oldIndex ? newIndex : i + 1;
                    orders[i] = { ...orders[i], ordinal: newOrdinal };
                }
            }

            // update backend & calculate new pricing
            dispatch(handleOnSortOrder(oldIndex, newIndex));
        }
    }

    const handleOrders = () => {
        setDropOffForm(prevDropOffForm => ({
            ...prevDropOffForm,
            recipientName: "",
            recipientPhone: "",
            deliverDateTime: "",
            dropOffLocation: {
                unitNumber: "",
                address: "",
                coordinate: {
                    latitude: "",
                    longitude: ""
                },
                city: "",
                country: ""
            },
            doorToDoor: false,
            isCodEnabled: false,
            codAmount: 0,
        }));

        setAddStop(hasPendingGuestDropOffs());
    }

    const handleUseSavedPickUpAddress = (addr) => {
        let error = false;

        if (addr.pickUpContactNumber === "" || addr.pickUpPersonName === "") {
            error = true;
            setError(true);
            setErrorMessage("Please ensure that you have a valid pick-up contact number and pick-up person name in your saved address.");
            window.scrollTo(0, 0);
            return;
        }

        var formattedPhoneNumber = formatPhoneNumber(addr.pickUpContactNumber);

        if (!formattedPhoneNumber) {
            error = true;
            setError(true);
            setErrorMessage("Please ensure that you have a valid pick-up contact number in your saved address.");
            window.scrollTo(0, 0);
            return;
        }

        if (!error) {
            setPickUpForm(prevPickUpForm => ({
                ...prevPickUpForm,
                pickUpContactNumber: formattedPhoneNumber,
                pickUpPersonName: addr.pickUpPersonName,
                pickUpLocation: {
                    ...prevPickUpForm.pickUpLocation,
                    ...addr.location
                }
            }));

            setError(false);
            setErrorMessage('');
        }
    }

    const handleUseSavedDropOffAddress = (addr) => {
        let error = false;

        if (addr.recipientPhone === "" || addr.recipientName === "") {
            error = true;
            setError(true);
            setErrorMessage("Please ensure that you have a valid recipient contact number and recipient name in your saved address.");
            window.scrollTo(0, 0);
            return;
        }

        var formattedPhoneNumber = formatPhoneNumber(addr.recipientPhone);

        if (!formattedPhoneNumber) {
            error = true;
            setError(true);
            setErrorMessage("Please ensure that you have a valid recipient contact number in your saved address.");
            window.scrollTo(0, 0);
            return;
        }

        if (!error) {
            setDropOffForm(prevDropOffForm => ({
                ...prevDropOffForm,
                recipientPhone: formattedPhoneNumber,
                recipientName: addr.recipientName,
                dropOffLocation: {
                    ...prevDropOffForm.dropOffLocation,
                    ...addr.location
                }
            }));
            setEnterDropOffDetails(true);
            setError(false);
            setErrorMessage("");
        }
    }

    const selectedVoucherDiscountAmount = orderListState?.selectedVoucher?.promoCode?.discountAmount;
    const selectedVoucherDiscountPercentage = orderListState?.selectedVoucher?.promoCode?.discountPercentage;

    const orderGroup = orderListState.orderGroup;
    const orders = orderGroup.orders || [];
    const hasOrderGroup = orderGroup.id != null;
    const hasPickUpAddress = Boolean(pickUpForm.pickUpLocation.address);
    const hasPickUpDetails = Boolean(pickUpForm.pickUpPersonName && pickUpForm.pickUpContactNumber);
    const priced = hasOrderGroup && orders.length > 0;

    // the price only exists once the backend has costed the stops, so say what is
    // still missing rather than rendering nothing
    const gate = !hasPickUpAddress
        ? { title: "Add a pick-up location", hint: "Start by choosing where the parcel is collected from." }
        : !hasPickUpDetails
            ? { title: "Add the sender's details", hint: "We need a name and contact number before we can price the delivery." }
            : orders.length === 0
                ? { title: "Add a drop-off location", hint: "Your price appears as soon as the first stop is costed." }
                : null;

    // a stop only becomes an order once it is complete, so the one that can actually
    // block confirmation is the pending stop: an address typed but details not saved
    const pendingStopIncomplete = addStop
        && Boolean(dropOffForm.dropOffLocation.address)
        && !(dropOffForm.recipientName && dropOffForm.recipientPhone);
    const pendingStopNumber = orders.length + 1;

    const openPickUpDetails = () => {
        setEnterPickUpDetails(true);
        setEnterDropOffDetails(false);
        setEditOrderOrdinal("");
    };

    const closeDetails = () => {
        setEnterPickUpDetails(false);
        setEnterDropOffDetails(false);
        setEditOrderOrdinal("");
        setError(false);
        setErrorMessage('');
    };

    const editingDropOff = orders.length > 0 && editOrderOrdinal !== "" && orders[editOrderOrdinal];

    return (
        <>
            {orderListState.loading && <LoadingOverlay confirmingOrder={orderListState.disableConfirmOrderGroup} />}
            {orderListState.showImportModal && <ImportModal />}
            {orderListState.showSavedPickUpAddrModal && <SavedAddressModal type="PICK_UP" useSavedAddress={(addr) => handleUseSavedPickUpAddress(addr)} />}
            {orderListState.showSavedDropOffAddrModal && <SavedAddressModal type="DROP_OFF" useSavedAddress={(addr) => handleUseSavedDropOffAddress(addr)} />}
            {orderListState.showVoucherModal && <VoucherModal />}
            {orderListState.showPaymentModal && <PaymentModal />}
            {orderListState.showTopUpModal && <TopUpModal closeModal={() => dispatch(setShowTopUpModal(false))} />}

            <div className={styles.container}>
                <Topbar page="Place Order" />

                <OrderGroupContext.Provider value={{ pickUpForm, dropOffForm }}>
                    <OrderGroup
                        handlePickUpLocation={(location) => {
                            setPickUpForm(prevPickUpForm => ({
                                ...prevPickUpForm,
                                pickUpLocation: {
                                    ...prevPickUpForm.pickUpLocation,
                                    ...location
                                }
                            }));
                            openPickUpDetails();
                        }}
                        handlePickUpDateTime={(date) => setPickUpForm(prevPickUpForm => ({
                            ...prevPickUpForm,
                            pickUpDateTime: date
                        }))}
                        closePickUpModal={() => setEnterPickUpDetails(false)}
                        handleError={(error) => setError(error)}
                        handleErrorMsg={(msg) => setErrorMessage(msg)}
                    >
                        {(og) => (
                            <div className={`${styles.poPanel} ${isCompact && sheetCollapsed ? styles.poPanelCollapsed : ""}`}>
                                <div className={styles.poGrabBar} onClick={() => setSheetCollapsed(!sheetCollapsed)}>
                                    <i />
                                </div>

                                <div className={styles.poScroll} ref={panelScrollRef}>
                                    {
                                        error &&
                                        <Alert
                                            severity="error"
                                            style={{ marginBottom: 12, borderRadius: 10, textAlign: "left" }}
                                            onClose={() => { setError(false); setErrorMessage(''); }}>
                                            {errorMessage}
                                        </Alert>
                                    }

                                    {/* ---------- Vehicle ---------- */}
                                    <VehicleSelector
                                        value={og.pickUpForm.deliveryMethod}
                                        onChange={(deliveryMethod) => og.onFormValueChange({ name: "deliveryMethod", value: deliveryMethod })}
                                    />

                                    {/* ---------- Pick-up time ---------- */}
                                    <div className={styles.poSec}>
                                        <div className={styles.poSecHead}><h3>Pick-up time</h3></div>
                                        <div className={styles.poCard}>
                                            <div className={styles.poTimeRow}>
                                                <img src={clockIcon} alt="" />
                                                <DateTimeField
                                                    inputId="pickUpDateTime"
                                                    className={styles.poAddrInput}
                                                    value={og.pickUpForm.pickUpDateTime}
                                                    onChange={date => {
                                                        og.validatePickUpDateTime(date);
                                                        if (hasOrderGroup) og.setFormSubmitted(false);
                                                    }}
                                                    minDate={new Date()}
                                                    minTime={setHours(setMinutes(new Date(), 0), 6)}
                                                    maxTime={setHours(setMinutes(new Date(), 0), 23)}
                                                    placeholder="Preferred pick-up time"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* ---------- Preferences ---------- */}
                                    <div className={styles.poSec}>
                                        <div className={styles.poSecHead}><h3>Preferences</h3></div>
                                        <div className={styles.poCard}>
                                            <div className={styles.poOptRow}>
                                                <img src={halalIcon} alt="" />
                                                <div className={styles.poOptTxt}>
                                                    <strong>Halal</strong>
                                                    <span>Match only riders with halal handling</span>
                                                </div>
                                                <div style={{ marginLeft: "auto" }}>
                                                    <TealSwitch
                                                        checked={og.pickUpForm.halal}
                                                        onChange={() => og.onFormValueChange({ name: "halal", value: !og.pickUpForm.halal })}
                                                        size="small"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ---------- Route ---------- */}
                                    <div className={styles.poSec}>
                                        <div className={styles.poSecHead}>
                                            <h3>Route</h3>
                                            <div className={styles.poSecTools}>
                                                <InfoTooltip
                                                    title={orders.length > 0 ? "Remove existing drop-offs to import a bulk order." : "Import drop-offs from a spreadsheet."}
                                                    placement="top"
                                                    arrow>
                                                    <div>
                                                        <button
                                                            className={styles.poTool}
                                                            disabled={orders.length > 0}
                                                            onClick={() => {
                                                                if (orderGroup.id != null) {
                                                                    dispatch(setShowImportModal(true));
                                                                } else {
                                                                    setError(true);
                                                                    setErrorMessage("Please select a pick-up location and fill in all required fields before importing bulk order.");
                                                                }
                                                            }}>
                                                            <img src={importIcon} alt="" style={{ width: 10, height: 10, objectFit: "contain" }} />
                                                            Import bulk
                                                        </button>
                                                    </div>
                                                </InfoTooltip>
                                            </div>
                                        </div>

                                        <div className={styles.poCard}>
                                            {/* pick-up stop */}
                                            <div className={`${styles.poStop} ${enterPickUpDetails ? styles.poStopActive : ""}`}>
                                                <div className={styles.poRail}>
                                                    <div className={`${styles.poPin} ${styles.poPinPickUp}`}>P</div>
                                                    <div className={styles.poThread} />
                                                </div>

                                                <div className={styles.poStopMain}>
                                                    <input
                                                        className={styles.poAddrInput}
                                                        id="pickUpAutocomplete"
                                                        name="pickUpAddress"
                                                        value={og.pickUpForm.pickUpLocation.address}
                                                        onChange={(e) => og.setPickUpForm({
                                                            ...og.pickUpForm,
                                                            pickUpLocation: {
                                                                ...og.pickUpForm.pickUpLocation,
                                                                address: e.target.value
                                                            }
                                                        })}
                                                        placeholder="Pick-up location"
                                                        autoComplete="off"
                                                    />

                                                    <div className={styles.poMeta} onClick={openPickUpDetails}>
                                                        {
                                                            og.pickUpForm.pickUpPersonName
                                                                ? <span className={styles.poMetaText}>
                                                                    {og.pickUpForm.pickUpPersonName}&nbsp;&nbsp;·&nbsp;&nbsp;{og.pickUpForm.pickUpContactNumber}
                                                                    {og.pickUpForm.pickUpLocation.unitNumber ? `  ·  ${og.pickUpForm.pickUpLocation.unitNumber}` : ""}
                                                                </span>
                                                                : <span className={styles.poMissing}>
                                                                    <i className="fas fa-exclamation-circle" style={{ fontSize: 12 }} />
                                                                    Add contact details
                                                                </span>
                                                        }
                                                        <StopSummaryTooltip rows={buildPickUpSummary(og.pickUpForm)} />
                                                    </div>
                                                </div>

                                                <div className={styles.poStopActs}>
                                                    {
                                                        !hasOrderGroup &&
                                                        <>
                                                            <InfoTooltip title="Use my current location" placement="top" arrow>
                                                                <button className={styles.poIco} onClick={og.handleUseCurrentLocation}>
                                                                    <img src={targetIcon} alt="current location" />
                                                                </button>
                                                            </InfoTooltip>
                                                            <InfoTooltip title="Saved pick-up addresses" placement="top" arrow>
                                                                <button className={styles.poIco} onClick={og.openSavedAddresses}>
                                                                    <img src={savedAddressIcon} alt="saved addresses" />
                                                                </button>
                                                            </InfoTooltip>
                                                        </>
                                                    }
                                                </div>
                                            </div>

                                            {/* drop-off stops */}
                                            {
                                                orders.length > 0 &&
                                                <SortableList
                                                    shouldCancelStart={(e) =>
                                                        Boolean(e.target.closest && e.target.closest(`input, button, .${styles.poMeta}`))
                                                    }
                                                    onSortEnd={onSortEnd}
                                                    orderListState={orderListState}
                                                    editOrderOrdinal={editOrderOrdinal}
                                                    addStop={addStop}
                                                    showDropOffModal={(ordinal) => {
                                                        setEnterPickUpDetails(false);
                                                        setEnterDropOffDetails(false);
                                                        setEditOrderOrdinal(ordinal);
                                                    }}
                                                    handleAddressChange={(location) =>
                                                        setDropOffForm(prevDropOffForm => ({
                                                            ...prevDropOffForm,
                                                            dropOffLocation: {
                                                                ...prevDropOffForm.dropOffLocation,
                                                                ...location,
                                                            },
                                                        }))
                                                    }
                                                    handleOrders={handleOrders}
                                                    handleError={(err, errMsg) => {
                                                        setError(err);
                                                        setErrorMessage(errMsg);
                                                    }}
                                                />
                                            }

                                            {/* new drop-off */}
                                            {
                                                addStop &&
                                                <OrderContext.Provider value={dropOffForm}>
                                                    <Order
                                                        addStop={addStop}
                                                        currIdx={orders.length}
                                                        editOrder={enterDropOffDetails}
                                                        nextOrdinal={orderGroup.nextOrdinal}
                                                        showThread={false}
                                                        showDropOffModal={() => {
                                                            setEnterPickUpDetails(false);
                                                            setEnterDropOffDetails(true);
                                                            setEditOrderOrdinal("");
                                                        }}
                                                        closeDropOffModal={() => setEnterDropOffDetails(false)}
                                                        handleAddressChange={(location) =>
                                                            setDropOffForm(prevDropOffForm => ({
                                                                ...prevDropOffForm,
                                                                dropOffLocation: {
                                                                    ...prevDropOffForm.dropOffLocation,
                                                                    ...location,
                                                                }
                                                            }))
                                                        }
                                                        handleOrders={(order) => handleOrders(order)}
                                                        handleError={(err, errMsg) => {
                                                            setError(err);
                                                            setErrorMessage(errMsg);
                                                        }}
                                                    />
                                                </OrderContext.Provider>
                                            }

                                            <button
                                                className={styles.poAddStop}
                                                onClick={() => { setAddStop(true); setEditOrderOrdinal(""); }}>
                                                <img src={plusIcon} alt="" />
                                                Add stop
                                            </button>
                                        </div>
                                    </div>

                                    {/* ---------- Routing ---------- */}
                                    {
                                        isRouteOptimizationEnabled() &&
                                        <div className={styles.poSec}>
                                            <div className={styles.poSecHead}><h3>Routing</h3></div>
                                            <div className={styles.poCard}>
                                                <div className={styles.poOptRow}>
                                                    <i className={`fas fa-route ${styles.poOptIco}`} />
                                                    <div className={styles.poOptTxt}>
                                                        <strong>Optimise route</strong>
                                                        <span>{orders.length >= 3 ? "Reorder the stops for the cheapest route." : "Available from 3 drop-offs."}</span>
                                                    </div>
                                                    <button
                                                        className={styles.poMiniCta}
                                                        disabled={!hasOrderGroup || orders.length < 3}
                                                        onClick={() => dispatch(handleOptimizeRoute())}>
                                                        Optimise
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>

                                {/* ---------- Price footer ---------- */}
                                <div className={styles.poFoot}>
                                    {
                                        gate
                                            ? <div className={styles.poGate}>
                                                <img src={locationPinIcon} alt="" />
                                                <div className={styles.poGateTxt}>
                                                    <strong>{gate.title}</strong>
                                                    <span>{gate.hint}</span>
                                                </div>
                                            </div>
                                            : <>
                                                <div className={styles.voucherContainer} style={{ marginBottom: 10 }}>
                                                    <img className={styles.icon} src={voucherIcon} alt="voucher" />
                                                    {
                                                        orderListState.selectedVoucher
                                                            ? <p className={styles.blackText}>{orderListState.selectedVoucher.promoCode.title || "Applied Voucher"}</p>
                                                            : <p className={styles.greyText}>Enter voucher</p>
                                                    }
                                                    <InfoTooltip
                                                        title="When using a promo code, please note that the discount amount will only be applied upon the completion of order (subject to promo code availability)."
                                                        placement="top"
                                                        arrow>
                                                        <div>
                                                            <i className="fas fa-info-circle" style={{ marginInline: 10, color: "#e0e0e0", fontSize: 18 }} />
                                                        </div>
                                                    </InfoTooltip>
                                                    {
                                                        orderListState.selectedVoucher ? (
                                                            <div style={{ display: "flex", marginLeft: "auto", alignItems: "center" }}>
                                                                <p className={styles.greenText}>
                                                                    {selectedVoucherDiscountPercentage
                                                                        ? (`- ${selectedVoucherDiscountPercentage}%`)
                                                                        : (`- ${orderGroup.currency} ${(selectedVoucherDiscountAmount != null ? (selectedVoucherDiscountAmount / 100).toFixed(2) : '0.00')}`)
                                                                    }
                                                                </p>
                                                                <img className={styles.icon} style={{ width: 12, cursor: "pointer" }} src={closeIcon} alt="close"
                                                                    onClick={() => { dispatch(setSelectedVoucher(null)); dispatch(getEstimatedTotal()) }} />
                                                            </div>
                                                        ) : (
                                                            <div className={styles.selectVoucherButton} onClick={() => dispatch(setShowVoucherModal(true))}>
                                                                <p style={{ margin: 0 }}>Select</p>
                                                            </div>
                                                        )
                                                    }
                                                </div>

                                                <div className={styles.poLines}>
                                                    <div>
                                                        <span>Subtotal{orders.length > 0 ? ` (${orders.length} ${orders.length === 1 ? "stop" : "stops"})` : ""}</span>
                                                        <span>{orderGroup.currency}{(orderGroup.subtotal / 100).toFixed(2)}</span>
                                                    </div>
                                                    {
                                                        orderListState.selectedVoucher &&
                                                        <div className={styles.poDiscount}>
                                                            <span>Voucher applied</span>
                                                            <span>-{orderGroup.currency}{(orderGroup.discountAmount / 100).toFixed(2)}</span>
                                                        </div>
                                                    }
                                                </div>

                                                <div className={styles.poTotal}>
                                                    <p className={styles.poTotalLbl}>Total</p>
                                                    <p className={styles.poTotalVal}>
                                                        <small>{orderGroup.currency}</small>{(orderGroup.estimatedTotal / 100).toFixed(2)}
                                                    </p>
                                                </div>
                                            </>
                                    }

                                    <button
                                        className={styles.poCta}
                                        disabled={!priced || error || pendingStopIncomplete}
                                        onClick={() => dispatch(setShowPaymentModal(true))}>
                                        {
                                            priced
                                                ? `Confirm order · ${orderGroup.currency}${(orderGroup.estimatedTotal / 100).toFixed(2)}`
                                                : "Confirm order"
                                        }
                                    </button>

                                    <p className={`${styles.poCtaHint} ${pendingStopIncomplete ? styles.poCtaHintBlocked : ""}`}>
                                        {
                                            pendingStopIncomplete
                                                ? `Stop ${pendingStopNumber} still needs a name and contact number.`
                                                : "Nothing is charged until you confirm."
                                        }
                                    </p>
                                </div>
                            </div>
                        )}
                    </OrderGroup>
                </OrderGroupContext.Provider>

                {/* ---------- Map ---------- */}
                <div className={styles.map}>
                    {
                        enterPickUpDetails &&
                        <StopDetailsPopover
                            type="PICK_UP"
                            stopKey="pick-up"
                            ordinal="P"
                            form={pickUpForm}
                            onCancel={closeDetails}
                            onSave={onPickUpDetailsChange}
                        />
                    }
                    {
                        enterDropOffDetails && addStop &&
                        <StopDetailsPopover
                            type="DROP_OFF"
                            stopKey="new-drop-off"
                            ordinal={orders.length + 1}
                            form={{
                                ...dropOffForm,
                                pickUpDateTime: pickUpForm.pickUpDateTime,
                                codAmount: (dropOffForm.codAmount / 100).toFixed(2)
                            }}
                            onCancel={closeDetails}
                            onSave={onDropOffDetailsChange}
                        />
                    }
                    {
                        editingDropOff &&
                        <StopDetailsPopover
                            type="DROP_OFF"
                            stopKey={`order-${orders[editOrderOrdinal].id}`}
                            ordinal={Number(editOrderOrdinal) + 1}
                            form={{
                                ...orders[editOrderOrdinal],
                                pickUpDateTime: pickUpForm.pickUpDateTime,
                                isCodEnabled: orders[editOrderOrdinal].codAmount > 0,
                                codAmount: orders[editOrderOrdinal].codAmount
                                    ? (orders[editOrderOrdinal].codAmount / 100).toFixed(2)
                                    : 0
                            }}
                            onCancel={closeDetails}
                            onSave={onDropOffDetailsChange}
                        />
                    }

                    {
                        orderListState.directions.length > 0
                            ? <PlaceOrderMap
                                loadingElement={<div style={{ height: `100%` }} />}
                                containerElement={<div style={{ height: "100%" }} />}
                                mapElement={<div style={{ height: `100%` }} />}
                                dispatch={dispatch}
                                orderListState={orderListState}
                                directions={orderListState.directions}
                                showPickUpModal={openPickUpDetails}
                                showDropOffModal={(ordinal) => {
                                    setEnterPickUpDetails(false);
                                    setEditOrderOrdinal(ordinal);
                                }}
                            />
                            : <Map
                                loadingElement={<div style={{ height: `100%` }} />}
                                containerElement={<div style={{ height: "100%" }} />}
                                mapElement={<div style={{ height: `100%` }} />}
                                center={mapFocus}
                                zoom={SELECTED_ADDRESS_ZOOM}
                                marker={mapFocus}
                            />
                    }
                </div>
            </div>
        </>
    )
}
